import {
  fetchBuyRateCurrency,
  fetchSellRateCurrency,
} from 'components/FXCalculatorForm/func';
import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import {
  CURRENCY_FLAG_REFERENCES,
  DESKTOP_MIN_WIDTH,
  FX_CALCULATOR_FORM_CONVERT_FROM_NAME,
  FX_CALCULATOR_FORM_TO_NAME,
} from 'constants';
import { Fade } from 'helpers/animation';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils, numberUtils } from 'utils';

const SEARCH_NAME_FIELD = 'fx-calculator-search-currency';

const CustomCurrencyDropdown = ({
  setRate,
  currentTab,
  currencies,
  information,
  setInformation,
}) => {
  const { t } = useLang();

  const [searchValue, setSearchValue] = useState('');
  const [dataSearch, setDataSearch] = useState(null);
  const [currentSelected, setCurrentSelected] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const { currencyCode } = information['currentCurrencySelected'] || {};

  const NO_RESULT_TEXT = t('form_text_no_result');

  const convertFromAmount =
    information[FX_CALCULATOR_FORM_CONVERT_FROM_NAME] || '';
  const toAmount = information[FX_CALCULATOR_FORM_TO_NAME] || '';

  useEffect(() => {
    const item = currencies?.find((c) => c?.['currencyCode'] === currencyCode);
    setCurrentSelected(item);
  }, [currencyCode, currencies]);

  useInputFocus(SEARCH_NAME_FIELD);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setDataSearch(null);

      return;
    }

    const newDataSearch = currencies?.filter(
      (item) =>
        (item?.['currencyCode']
          ?.toLowerCase()
          ?.includes(value?.toLowerCase()) ||
          item?.['currency']?.toLowerCase()?.includes(value?.toLowerCase())) &&
        item
    );
    setDataSearch(newDataSearch);
  };

  const handleItemSelected = async (currency) => {
    setCurrentSelected(currency);

    setSearchValue('');

    setDataSearch(null);

    setShowDropdown(false);

    let newInformation = {
      ...information,
      currentCurrencySelected: currency,
    };

    let currentCurrency;
    let currentRate;
    let toValue = 0;
    let convertFromValue = 0;

    const currentMinDenomination = currency?.minDenomination || 1;

    if (currentTab === 0) {
      currentCurrency = await fetchSellRateCurrency(currency?.currencyCode);

      currentRate = currentCurrency?.rate;

      toValue =
        Math.round((convertFromAmount * currentRate) / currentMinDenomination) *
        currentMinDenomination;

      convertFromValue = numberUtils.mathRound(
        (toValue / currentRate).toFixed(2)
      );

      newInformation = {
        ...newInformation,
        [FX_CALCULATOR_FORM_CONVERT_FROM_NAME]: convertFromValue,
        [FX_CALCULATOR_FORM_TO_NAME]: toValue,
      };
    }

    if (currentTab === 1) {
      currentCurrency = await fetchBuyRateCurrency(currency?.currencyCode);

      currentRate = currentCurrency?.rate;

      convertFromValue =
        Math.round((toAmount * currentRate) / currentMinDenomination) *
        currentMinDenomination;

      toValue = numberUtils.mathRound(
        (convertFromValue / currentRate).toFixed(2)
      );

      newInformation = {
        ...newInformation,
        [FX_CALCULATOR_FORM_CONVERT_FROM_NAME]: convertFromValue,
        [FX_CALCULATOR_FORM_TO_NAME]: toValue,
      };
    }

    setRate(currentCurrency?.rate);

    setInformation(newInformation);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');

    setDataSearch(null);

    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownToggleChildren = () => (
    <>
      {currentSelected && (
        <CountryName>
          <FlagIcon
            src={CURRENCY_FLAG_REFERENCES[currentSelected?.currencyCode]}
          />
          {currentSelected?.currencyCode}
        </CountryName>
      )}
      <ArrowDownIcon
        color="#FFFFFF"
        styles={{
          position: 'absolute',
          right: '8px',
          top: '22px',
          transform: isShowDropdown && 'rotate(180deg)',
        }}
      />
    </>
  );
  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        name={SEARCH_NAME_FIELD}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{
          paddingBottom: 0,
          borderRadius: '0 0 8px 8px',
          minWidth: '100%',
        }}
      >
        {renderDropdownItems(dataSearch || currencies)}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
          style={{
            borderRadius: '8px',
            height: '50px',
          }}
        >
          <NoItemText>{NO_RESULT_TEXT}</NoItemText>
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.currencyCode}
            eventKey={index}
            onClick={() => handleItemSelected(item)}
            style={{
              borderRadius: '8px',
              height: '50px',
            }}
          >
            <CountryNameItem
              style={{
                color: '#0E1012',
              }}
            >
              <FlagIcon src={CURRENCY_FLAG_REFERENCES[item.currencyCode]} />
              {`${item.currency} (${item.currencyCode})`}
            </CountryNameItem>
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  const getStyleDropdown = () => {
    const common = { border: 'none' };
    return common;
  };
  const getStyleDropdownToggle = () => {
    const common = {
      textTransform: 'initial',
      paddingRight: '40px',
      fontWeight: 600,
      minHeight: '50px',
    };
    return common;
  };
  const getStyleDropdownMenu = () => {
    const common = {
      borderRadius: '8px',
      background: '#fff',
      minWidth: '123px',
      transform: 'translate(60px, 51px)',
      width: 'calc(100dvw - 31px)',
      left: 'calc(-100dvw + 32px)',
    };
    return common;
  };

  return (
    <CurrencyDropdownStyled>
      <DropdownCommon
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownToggleChildren={renderDropdownToggleChildren}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
        styleDropdown={getStyleDropdown()}
        styleDropdownToggle={getStyleDropdownToggle()}
        styleDropdownMenu={getStyleDropdownMenu()}
      />
    </CurrencyDropdownStyled>
  );
};

const CurrencyDropdownStyled = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 27px;
  right: 10.5px;
  ${Fade};
`;

const CountryName = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-white);

  width: 123px;
  height: 50px;
  background-color: var(--ds-c-black);
  justify-content: flex-end;
  align-items: center;
  padding-right: 46px;
  display: flex;
  position: absolute;
  top: 1px;
  right: 0.5px;
  margin-right: -10px;
  border-radius: 0 11px 11px 0px;
`;
const CountryNameItem = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--c-primary);

  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;
const FlagIcon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  object-fit: cover;
  border: 1px solid var(--ds-bg-2);
  border-radius: 50%;
  margin-right: 8px;
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;
  border-radius: 0 0 8px 8px;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default CustomCurrencyDropdown;
